import { QueryClient, type QueryFunction } from '@tanstack/react-query';
import { isCancel } from 'axios';
import { join } from 'lodash';
import { ZodError } from 'zod';

import { httpStatusOf } from '../utils/httpStatusOf';
import { extractPathAndParams } from '../utils/objectUtil';
import { apiGet } from './apiRequest';

export { DEFAULT_TIMEOUT } from './apiRequest';
export const DEFAULT_STALE_TIME = 5 * 60 * 1000;
export const DEFAULT_RETRY_NUMBERS = 3;
export const DEFAULT_RETRY_DELAY = 3000;

export const shouldRetryQuery = (failureCount: number, error: unknown): boolean => {
  if (error instanceof ZodError || isCancel(error)) return false;
  const status = httpStatusOf(error);
  if (status && [400, 401, 403, 404, 422].includes(status)) return false;
  return failureCount < 2;
};

export type QueryWithError = { state: { error: unknown } };

export const refetchUnlessServerError = (query?: QueryWithError): boolean => {
  const status = httpStatusOf(query?.state?.error);
  if (status && status >= 500 && status < 600) return false;
  return true;
};

export const defaultQueryFn: QueryFunction = async ({ queryKey, meta }) => {
  const [primitives, mergedParams] = extractPathAndParams(queryKey);
  return apiGet(`/api/${join(primitives, '/')}`, mergedParams, meta?.timeout as number | undefined);
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: defaultQueryFn,
      staleTime: DEFAULT_STALE_TIME,
      refetchOnWindowFocus: refetchUnlessServerError,
      refetchOnMount: refetchUnlessServerError,
      retry: shouldRetryQuery,
    },
  },
});
